import { createContext, ReactNode, use, useCallback, useEffect, useState } from 'react';
import {
  addListener,
  Listener,
  localStorageGet,
  LocalStorageKey,
  localStorageSet,
  LocalStorageType,
  removeListener,
} from './client_local_storage.ts';

export function useLocalStorage<T extends LocalStorageKey>(
  key: T,
): [
  value: LocalStorageType<T>,
  setValue: (
    newValueOrFunction: LocalStorageType<T> | ((currentValue: LocalStorageType<T>) => LocalStorageType<T>),
  ) => void,
] {
  const { localStorageGetCtx, localStorageSetCtx, addListenerCtx, removeListenerCtx } = use(LocalStorageContext);
  const localStorageGetLocal = localStorageGetCtx ?? localStorageGet;
  const localStorageSetLocal = localStorageSetCtx ?? localStorageSet;
  const addListenerLocal = addListenerCtx ?? addListener;
  const removeListenerLocal = removeListenerCtx ?? removeListener;
  const [value, setValue] = useState<LocalStorageType<T>>(() => localStorageGetLocal(key));

  // We intentionally do not use useSyncExternalStore here: localStorageGetLocal goes through
  // JSON.parse and returns a fresh object reference on every call. useSyncExternalStore requires
  // getSnapshot to return the same reference between calls when the underlying value hasn't changed
  // (otherwise React's tearing-detection re-read during concurrent rendering would see "new" values
  // on every pass and re-render in an infinite loop). Caching to satisfy that contract is more
  // complexity than this core hook warrants, so we stick with useState + a listener-driven effect.
  useEffect(() => {
    // eslint-react flags setValue inside an effect, but the synchronous refreshValue() below is
    // load-bearing: it closes the race between useState initialization and listener subscription
    // (without it, a write in that window would be lost). The async setValue calls fired later by
    // the listener are the normal external-store pattern and not what the rule targets.
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    const refreshValue = () => setValue(localStorageGetLocal(key));
    addListenerLocal(key, refreshValue);
    refreshValue();
    return () => removeListenerLocal(key, refreshValue);
  }, [addListenerLocal, key, localStorageGetCtx, localStorageGetLocal, removeListenerLocal]);

  const updateValue = useCallback(
    (newValueOrFunction: LocalStorageType<T> | ((currentValue: LocalStorageType<T>) => LocalStorageType<T>)) => {
      let newValue: LocalStorageType<T>;
      if (typeof newValueOrFunction === 'function') {
        newValue = newValueOrFunction(localStorageGetLocal(key));
      } else {
        newValue = newValueOrFunction;
      }
      localStorageSetLocal(key, newValue);
    },
    [key, localStorageGetLocal, localStorageSetLocal],
  );

  return [value, updateValue];
}

export interface LocalStorageContextInterface {
  localStorageGetCtx<T extends LocalStorageKey>(key: T, defaultOverride?: LocalStorageType<T>): LocalStorageType<T>;
  localStorageSetCtx<T extends LocalStorageKey>(key: T, value: LocalStorageType<T>): void;
  addListenerCtx<T extends LocalStorageKey>(key: T, listener: Listener): void;
  removeListenerCtx<T extends LocalStorageKey>(key: T, listener: Listener): void;
}

export const LocalStorageContext = createContext<LocalStorageContextInterface>({} as any);

export const LocalStorageProvider = ({ children }: { children: ReactNode }) => {
  return (
    <LocalStorageContext
      value={{
        localStorageGetCtx: localStorageGet,
        localStorageSetCtx: localStorageSet,
        addListenerCtx: addListener,
        removeListenerCtx: removeListener,
      }}
    >
      {children}
    </LocalStorageContext>
  );
};
